import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  selectDebouncedSearchQuery,
  selectGroupingConfig,
  selectPagination,
  selectApiData,
  selectBacklogViewType,
} from "@/features/bughurd/boardSelector";
import BacklogBoardView from "./BacklogBoardView";
import { useParams, useNavigate } from "react-router-dom";
import { useBoardFilters } from "./custom-hooks/useBoardFilters";
import { useDragAndDrop } from "./custom-hooks/useDragAndDropTask";
import { useBulkMove } from "./custom-hooks/useBulkMove";
import {
  useTheme,
  Menu,
  MenuItem,
  Box,
  Typography,
  Skeleton,
  Paper,
  Chip,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TaskUpdateDrawer from "../bugherdComponents/TaskUpdateDrawer";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import TaskAccordion from "./TaskAccordion";
import {
  useUpdateBugDetailMutation,
  useDeleteBugMutation,
  useCreateSprintMutation,
  useCreateBugMutation,
  useGetListOfBugsQuery,
  useLazyGetListOfBugsQuery,
  useGetSprintListQuery,
  useUpdateSprintMutation,
  useGetSprintByIdQuery,
  useDeleteSprintMutation,
  useUpdateSprintStatusMutation,
  useGetListOfStatusQuery,
} from "@/features/bughurd/api";
import { useGetCompanyMembersQuery } from "@/features/Members/api";
import SprintStartModal from "./Sprint/SprintStartModal";
import SprintCompleteModal from "./Sprint/SprintCompleteModal";
import {
  setActiveSprintIds,
  setActiveSprints,
  setBacklogViewType,
  setCreateTask,
  setSelectedItem,
  setViewType,
  updateTask,
} from "@/features/bughurd/slice";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Drag and Drop imports
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import BacklogMenus from "./BacklogMenus";
import TaskDataGrid from "./TaskDataGrid";

export interface BugItem {
  _id: string;
  bugKey: string;
  summary: string;
  assignTo: Array<{
    _id: string;
    fullName: string;
  }>;
  bugStatus: {
    _id: string;
    statusCode: string;
    status: string;
  };
  severity: string;
  type: string;
  createdBy: {
    _id: string;
    fullName: string;
  };
  project: {
    _id: string;
    name: string;
    code: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface Sprint {
  _id: string;
  name: string;
  status: string;
  startDate?: string;
  endDate?: string;
  goal?: string;
  // Add these new properties
  sprintKey?: string;
  description?: string;
  totalBugCount?: number;
  doneBugCount?: number;
}

const BacklogTab = ({}) => {
  const { t } = useTranslation(["bugherd"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const issueType = [
    { value: "STORY", label: t("Story") },
    { value: "BUG", label: t("Defect") },
    { value: "EPIC", label: t("Epic") },
    { value: "SUGGESTIONS", label: t("Suggestions") },
  ];
  // Redux selectors
  const { companyMemberData, projectDetails } = useAppSelector(selectApiData);

  const { recordsPerPage } = useAppSelector((state) => state.appGlobal);
  const { filters } = useBoardFilters();
  const debouncedSearchQuery = useAppSelector(selectDebouncedSearchQuery);
  const backlogViewType = useAppSelector(selectBacklogViewType);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: recordsPerPage || 10,
    page: 1,
  });
  const [viewMode, setViewMode] = useState<"epics" | "stories" | "all">("all");
  const [columnPaginationState, setColumnPaginationState] = useState<{
    [columnId: string]: {
      page: number;
      limit: number;
      isLoadingMore: boolean;
    };
  }>({
    backlog: { page: 1, limit: 10, isLoadingMore: false },
  });
  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: "epics" | "stories" | "all" | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setPaginationModel({ page: 1, pageSize: 10 });
    }
  };

  // Extract filters
  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    selectedCreatedBy,
  } = filters;

  // Local state for modals and menus
  const [menuAnchorEl, setMenuAnchorEl] = useState<
    | HTMLElement
    | { element: HTMLElement; position: { top: number; left: number } }
    | null
  >(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [actionTaskId, setActionTaskId] = useState<string | null>(null);
  const [isDeleteSprintModalOpen, setIsDeleteSprintModalOpen] = useState(false);
  const [sprintToDelete, setSprintToDelete] = useState<Sprint | null>(null);
  const [isEditSprintModalOpen, setIsEditSprintModalOpen] = useState(false);
  const [editingStatusTaskId, setEditingStatusTaskId] = useState<string | null>(
    null
  );
  const [newTaskTypes, setNewTaskTypes] = useState<{ [key: string]: string }>(
    {}
  );
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [isSprintSectionCollapsed, setIsSprintSectionCollapsed] =
    useState(true);
  const [selectedSprintForEdit, setSelectedSprintForEdit] =
    useState<Sprint | null>(null);
  const [expandedAccordions, setExpandedAccordions] = useState<{
    [key: string]: boolean;
  }>({
    backlog: true,
  });

  // Create task states
  const [isCreatingTask, setIsCreatingTask] = useState<{
    [key: string]: boolean;
  }>({});
  const [newTaskNames, setNewTaskNames] = useState<{ [key: string]: string }>(
    {}
  );

  // Sprint task data state
  const [sprintTasksData, setSprintTasksData] = useState<{
    [key: string]: any;
  }>({});

  const [sprintPaginationModels, setSprintPaginationModels] = useState<{
    [key: string]: { page: number; pageSize: number };
  }>({});
  const [isSprintStartModalOpen, setIsSprintStartModalOpen] = useState(false);
  const [selectedSprintForStart, setSelectedSprintForStart] =
    useState<Sprint | null>(null);
  const [sprintMenuAnchorEl, setSprintMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [isSprintCompleteModalOpen, setIsSprintCompleteModalOpen] =
    useState(false);
  const [selectedSprintForComplete, setSelectedSprintForComplete] =
    useState<Sprint | null>(null);

  // Row selection states
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [containerSelections, setContainerSelections] = useState<{
    [key: string]: Set<string>;
  }>({});

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  const { data: statusListData } = useGetListOfStatusQuery(
    { projectId: id },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const statusList = statusListData?.data?.decryptedData?.items || [];
  const filterPayload = useMemo(
    () => ({
      search: debouncedSearchQuery || "",
      assignTo: selectedAvatars?.length > 0 ? selectedAvatars : undefined,
      tags: selectedTags?.length > 0 ? selectedTags : undefined,
      statuses: selectedStatuses?.length > 0 ? selectedStatuses : undefined,
      severities:
        selectedPriorities?.length > 0 ? selectedPriorities : undefined,
      createdBy: selectedCreatedBy?.length > 0 ? selectedCreatedBy : undefined,
      // showEpic: true,
      // viewType: "BACKLOG",
      // isBackLog: true,
      ...(dueDateStart && { dueDateStart }),
      ...(dueDateEnd && { dueDateEnd }),
      ...(startDateStart && { startDateStart }),
      ...(startDateEnd && { startDateEnd }),
      ...(updatedAtStart && { updatedAtStart }),
      ...(updatedAtEnd && { updatedAtEnd }),
    }),
    [
      debouncedSearchQuery,
      selectedAvatars,
      selectedTags,
      selectedStatuses,
      selectedPriorities,
      dueDateStart,
      dueDateEnd,
      startDateStart,
      startDateEnd,
      updatedAtStart,
      updatedAtEnd,
      selectedCreatedBy,
    ]
  );
  const getBacklogQueryParams = () => {
    const baseParams = {
      id: id!,
      forBoard: true,
      ...filterPayload,
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      isBackLog: true,
      sortType: "DESC",
      // viewType: "BACKLOG",
    };

    if (viewMode === "epics") {
      return { ...baseParams, multiType: "EPIC", showEpic: true };
    }
    if (viewMode === "stories") {
      return {
        ...baseParams,
        multiType: "BUG,STORY,DEFECT,SUGGESTIONS",
        showEpic: false,
      };
    }
    if (viewMode === "all") {
      return {
        ...baseParams,
        showEpic: true,
      };
    }
    return baseParams;
  };
  const {
    data: listOfBugsData,
    isLoading: isBugsLoadingQuery,
    isFetching: isBugsFetchingQuery,
    refetch: refetchBugList,
  } = useGetListOfBugsQuery(getBacklogQueryParams());
  // Get sprints
  const {
    data: sprintListData,
    isLoading: isSprintLoading,
    refetch: refetchSprints,
  } = useGetSprintListQuery({
    projectId: id || "",
    page: 1,
    limit: 100,
    statuses: ["DRAFT", "ACTIVE"],
  });

  // Lazy query for sprint tasks
  const [getSprintTasks] = useLazyGetListOfBugsQuery();

  // Additional API hooks for mutations
  const { data: users } = useGetCompanyMembersQuery(1);
  const [updateBug, { isLoading: isUpdating }] = useUpdateBugDetailMutation();
  const [deleteBug, { isLoading: isDeleting }] = useDeleteBugMutation();
  const [createSprint, { isLoading: isCreatingSprint }] =
    useCreateSprintMutation();
  const [createBug, { isLoading: isCreatingBug }] = useCreateBugMutation();
  const [updateSprint, { isLoading: isUpdatingSprint }] =
    useUpdateSprintMutation();
  const [updateSprintStatus, { isLoading: isUpdatingSprintStatus }] =
    useUpdateSprintStatusMutation();
  const [deleteSprint, { isLoading: isDeletingSprint }] =
    useDeleteSprintMutation();
  const [minimizedColumns, setMinimizedColumns] = useState<{
    [key: string]: boolean;
  }>({});

  // Add toggle handler
  const handleToggleMinimize = (columnId: string) => {
    setMinimizedColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };
  const sprints = sprintListData?.data?.decryptedData?.items || [];
  // Initialize drag and drop hook
  const {
    activeTask,
    isDragging,
    draggedTasks,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    findTask,
    getActiveContainer,
    getAllSelectedTasks,
    clearAllSelections,
  } = useDragAndDrop({
    backlogData: listOfBugsData,
    sprintTasksData,
    setSprintTasksData,
    updateBugMutation: updateBug,
    updateSprintStatusMutation: updateSprintStatus,
    refetchBugList,
    sprints,
    containerSelections,
    setContainerSelections,
  });

  // Initialize bulk move hook
  const {
    moveToMenuAnchorEl,
    setMoveToMenuAnchorEl,
    getAvailableMoveDestinations,
    handleBulkMove,
  } = useBulkMove({
    sprints,
    containerSelections,
    setContainerSelections,
    sprintTasksData,
    setSprintTasksData,
    updateSprintStatusMutation: updateSprintStatus,
    refetchBugList,
    findTask,
    getActiveContainer,
  });
  useEffect(() => {
    refetchBugList();
  }, [viewMode]);
  const handleSelectionChange =
    (containerId: string) => (selection: Set<string>) => {
      setContainerSelections((prev) => {
        const updated = { ...prev };
        if (selection.size === 0) {
          delete updated[containerId];
        } else {
          updated[containerId] = selection;
        }
        return updated;
      });
    };

  // Get total selected tasks count
  const totalSelectedTasks = getAllSelectedTasks().size;

  // Get active container (container with selections)
  const activeContainer = getActiveContainer();

  // Clear all selections
  const clearSelections = () => {
    setSelectedTasks(new Set());
    setContainerSelections({});
  };

  useEffect(() => {
    if (sprints.length > 0) {
      const activeSprints = getActiveSprints(sprints);
      dispatch(
        setActiveSprintIds(activeSprints.map((sprint: Sprint) => sprint._id))
      );
      dispatch(setActiveSprints(activeSprints));
    }
  }, [sprints]);
  // Initialize pagination state for sprints when they are loaded
  useEffect(() => {
    if (sprints.length > 0) {
      setColumnPaginationState((prev) => {
        const newState = { ...prev };
        sprints.forEach((sprint: Sprint) => {
          const sprintKey = `sprint-${sprint._id}`;
          if (!newState[sprintKey]) {
            newState[sprintKey] = {
              page: 1,
              limit: 10,
              isLoadingMore: false,
            };
          }
        });
        return newState;
      });
    }
  }, [sprints.length]);

  // FIXED: Load tasks for all sprints with consistent limit
  useEffect(() => {
    if (sprints.length > 0) {
      const expandedState: { [key: string]: boolean } = { backlog: true };

      sprints.forEach((sprint: Sprint) => {
        const sprintKey = `sprint-${sprint._id}`;
        expandedState[sprintKey] = true;
      });

      setExpandedAccordions(expandedState);

      sprints.forEach(async (sprint: Sprint) => {
        if (!sprintTasksData[sprint._id]) {
          try {
            const sprintKey = `sprint-${sprint._id}`;
            const limit = columnPaginationState[sprintKey]?.limit || 10;

            const response = await getSprintTasks({
              id: id || "",
              forBoard: true,
              sprint: sprint._id,
              ...filterPayload,
              page: 1,
              limit: limit,
            }).unwrap();

            setSprintTasksData((prev) => ({
              ...prev,
              [sprint._id]: response,
            }));
          } catch (error) {
            console.error("❌ Error loading sprint tasks:", error);
          }
        }
      });
    }
  }, [sprints.length, id]);

  // Reset pagination when filters change
  useEffect(() => {
    setColumnPaginationState((prev) => {
      const newState: any = {
        backlog: { page: 1, limit: 10, isLoadingMore: false },
      };

      // Reset all sprint pagination states
      Object.keys(prev).forEach((key) => {
        if (key.startsWith("sprint-")) {
          newState[key] = { page: 1, limit: 10, isLoadingMore: false };
        }
      });

      return newState;
    });

    // Reset backlog pagination model
    setPaginationModel((prev) => ({
      ...prev,
      page: 1,
      pageSize: 10,
    }));
  }, [filterPayload]);

  // UseEffect to reload sprint tasks when filters change
  useEffect(() => {
    if (sprints.length > 0) {
      sprints.forEach(async (sprint: Sprint) => {
        if (sprintTasksData[sprint._id]) {
          try {
            const response = await getSprintTasks({
              id: id || "",
              forBoard: true,
              sprint: sprint._id,
              ...filterPayload,
              page: 1,
              limit: 100,
            }).unwrap();

            setSprintTasksData((prev) => ({
              ...prev,
              [sprint._id]: response,
            }));
          } catch (error) {
            console.error("Error reloading sprint tasks:", error);
          }
        }
      });
    }
  }, [filterPayload]); // This will trigger when any filter changes

  // Add this useEffect for keyboard shortcut (optional)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "s" && backlogViewType === "backlog") {
        event.preventDefault();
        setIsSprintSectionCollapsed(!isSprintSectionCollapsed);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isSprintSectionCollapsed, backlogViewType]);

  const handleStatusClick = (
    event: React.MouseEvent<HTMLElement>,
    taskId: string
  ) => {
    event.stopPropagation();
    setEditingStatusTaskId(taskId);
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setEditingStatusTaskId(null);
    setStatusAnchorEl(null);
  };

  const handleStatusUpdate = async (newStatusId: string) => {
    if (!editingStatusTaskId) return;

    try {
      const payload = {
        bugId: editingStatusTaskId,
        bugStatus: newStatusId,
      };
      handleStatusClose();

      await updateBug(payload).unwrap();
      toast.success(t("Status updated successfully"));

      // Refresh data
      refetchBugList();
      Object.keys(sprintTasksData).forEach(async (sprintId) => {
        const response = await getSprintTasks({
          id: id || "",
          forBoard: true,
          sprint: sprintId,
          ...filterPayload,
          page: 1,
          limit: 100,
        }).unwrap();
        setSprintTasksData((prev) => ({
          ...prev,
          [sprintId]: response,
        }));
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(t("Failed to update status"));
      handleStatusClose();
    }
  };

  const handleCompleteSprint = async (
    event: React.MouseEvent,
    sprint: Sprint
  ) => {
    event.stopPropagation();
    setSelectedSprintForComplete(sprint);
    setIsSprintCompleteModalOpen(true);
  };

  const handleSprintComplete = async (data: any) => {
    try {
      const payload: any = {
        status: "ARCHIVE",
        sprintId: data.sprintId,
      };
      if (data.moveOpenItemsTo == "backlog") {
        payload.moveItems = "BACKLOG";
      } else if (data.moveOpenItemsTo == "new_sprint") {
        payload.moveItems = "NEW_SPRINT";
      } else {
        payload.moveItems = "EXISTING_SPRINT";
        payload.sprint = data.moveOpenItemsTo;
      }
      await updateSprintStatus(payload).unwrap();
      toast.success(t("Sprint completed successfully"));
      setIsSprintCompleteModalOpen(false);
      setSelectedSprintForComplete(null);
      await refetchSprints();

      if (data.moveOpenItemsTo === "backlog") {
        await refetchBugList();
      } else if (data.moveOpenItemsTo !== "new_sprint") {
        const targetSprintId = data.moveOpenItemsTo;
        try {
          const response = await getSprintTasks({
            id: id || "",
            forBoard: true,
            sprint: targetSprintId,
            page: 1,
            limit: 100,
          }).unwrap();
          setSprintTasksData((prev) => ({
            ...prev,
            [targetSprintId]: response,
          }));
        } catch (error) {
          console.error("Error refreshing target sprint tasks:", error);
        }
      }
    } catch (error) {
      console.error("Error completing sprint:", error);
      toast.error(t("Failed to complete sprint"));
    }
  };

  const handleCloseSprintCompleteModal = () => {
    setIsSprintCompleteModalOpen(false);
    setSelectedSprintForComplete(null);
  };

  const openDeleteSprintModal = (sprintId: string) => {
    const sprint = sprints.find((s: Sprint) => s._id === sprintId);
    if (sprint) {
      setSprintToDelete(sprint);
      setIsDeleteSprintModalOpen(true);
    }
  };

  const closeDeleteSprintModal = () => {
    setIsDeleteSprintModalOpen(false);
    setSprintToDelete(null);
  };

  const handleConfirmDeleteSprint = async () => {
    if (!sprintToDelete) return;

    try {
      await deleteSprint(sprintToDelete._id).unwrap();
      toast.success(t("Sprint deleted successfully"));

      // Refresh sprints list
      await refetchSprints();

      // Remove sprint tasks data from local state
      setSprintTasksData((prev) => {
        const updated = { ...prev };
        delete updated[sprintToDelete._id];
        return updated;
      });

      // Refresh backlog as tasks might have been moved back
      // await refetchBugList();

      // Close the modal
      closeDeleteSprintModal();
    } catch (error) {
      console.error("Error deleting sprint:", error);
      toast.error(t("Failed to delete sprint"));
      closeDeleteSprintModal();
    }
  };

  const getDraftSprints = () => {
    return sprints.filter((sprint: Sprint) => sprint.status === "DRAFT");
  };

  const getActiveSprints = (sprints: Sprint[]) => {
    return sprints.filter((sprint: Sprint) => sprint.status === "ACTIVE");
  };

  const getAvailableSprints = () => {
    return sprints.filter(
      (sprint: Sprint) =>
        sprint.status === "DRAFT" || sprint.status === "ACTIVE"
    );
  };

  const isBacklogTask = (taskId: string) => {
    return backlogRows.some((task) => task._id === taskId);
  };

  const handleMoveToSprint = async (sprintId: string) => {
    if (!actionTaskId) return;

    try {
      const payload = {
        bugId: actionTaskId,
        currentSprint: sprintId,
        isBackLog: false,
      };
      setSprintMenuAnchorEl(null);
      handleMenuClose();
      await updateBug(payload).unwrap();
      toast.success(t("Task moved to sprint successfully"));

      refetchBugList();

      try {
        const response = await getSprintTasks({
          id: id || "",
          forBoard: true,
          sprint: sprintId,
          ...filterPayload,
          page: 1,
          limit: 100,
        }).unwrap();

        setSprintTasksData((prev) => ({
          ...prev,
          [sprintId]: response,
        }));
      } catch (sprintError) {
        console.error("Error refreshing sprint tasks:", sprintError);
      }
    } catch (error) {
      console.error("Error moving task to sprint:", error);
      toast.error(t("Failed to move task to sprint"));
    }
  };

  // Update the existing loadSprintTasks function or add if missing
  const loadSprintTasks = async (
    sprintId: string,
    page: number = 1,
    limit: number = 10
  ) => {
    try {
      const response = await getSprintTasks({
        id: id || "",
        forBoard: true,
        sprint: sprintId,
        ...filterPayload,
        page: page,
        limit: limit,
      }).unwrap();

      setSprintTasksData((prev) => ({
        ...prev,
        [sprintId]: response,
      }));
    } catch (error) {
      console.error("Error loading sprint tasks:", error);
    }
  };

  const handleSprintPaginationChange =
    (sprintId: string) => (newModel: { page: number; pageSize: number }) => {
      setSprintPaginationModels((prev) => ({
        ...prev,
        [sprintId]: newModel,
      }));

      loadSprintTasksWithPagination(sprintId, newModel.page, newModel.pageSize);
    };

  const loadSprintTasksWithPagination = async (
    sprintId: string,
    page: number = 1,
    pageSize: number = 10
  ) => {
    try {
      const response = await getSprintTasks({
        id: id || "",
        forBoard: true,
        sprint: sprintId,
        ...filterPayload,
        page: page,
        limit: pageSize,
      }).unwrap();

      setSprintTasksData((prev) => ({
        ...prev,
        [sprintId]: response,
      }));
    } catch (error) {
      console.error("Error loading sprint tasks:", error);
    }
  };

  const getTodoStatusId = () => {
    const todoStatus = statusList?.find(
      (status: any) => status.statusCode === "TODO"
    );
    return todoStatus?._id || "";
  };

  const handleCreateTaskClick = (containerKey: string) => {
    setIsCreatingTask((prev) => ({ ...prev, [containerKey]: true }));
    setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));

    setTimeout(() => {
      const textField = document.querySelector(
        `input[data-testid="create-task-${containerKey}"]`
      ) as HTMLInputElement;
      if (textField) {
        textField.focus();
      }
    }, 0);
  };
  const handleTaskUpdate = async (
    taskId: string,
    updatedTask: any | null,
    field?: string,
    containerId?: string
  ) => {
    let val = {};

    switch (field) {
      case "assignTo":
        val = { assignTo: updatedTask?.assignTo?.map((user) => user._id) };
        break;
      case "type":
        val = { type: updatedTask?.type };
        break;
      case "bugStatus":
        val = { bugStatus: updatedTask?.bugStatus?._id };
        break;
      case "severity":
        val = { severity: updatedTask?.severity };
        break;
      case "summary":
        val = { summary: updatedTask?.summary };
        break;
      default:
        return; // No valid field, exit early
    }

    try {
      const payload = {
        bugId: taskId,
        ...val,
      };

      await updateBug(payload).unwrap();

      const successMessage =
        field === "assignTo"
          ? "Assignee updated successfully"
          : field === "type"
          ? "Type updated successfully"
          : field === "severity"
          ? "Priority updated successfully"
          : field === "summary"
          ? "Title updated successfully"
          : "Bug status updated successfully";

      toast.success(t(successMessage));

      // Refresh only the specific container
      if (containerId === "backlog") {
        // Only refresh backlog
        refetchBugList();
      } else if (containerId?.startsWith("sprint-")) {
        // Extract sprint ID and refresh only that sprint
        const sprintId = containerId.replace("sprint-", "");
        try {
          const response = await getSprintTasks({
            id: id || "",
            forBoard: true,
            sprint: sprintId,
            ...filterPayload,
            page: 1,
            limit: 100,
          }).unwrap();
          setSprintTasksData((prev) => ({
            ...prev,
            [sprintId]: response,
          }));
        } catch (error) {
          console.error("Error refreshing sprint tasks:", error);
        }
      }
    } catch (error) {
      console.error("Error updating:", error);

      const errorMessage =
        field === "assignTo"
          ? "Failed to update Assignee"
          : field === "type"
          ? "Failed to update Type"
          : field === "severity"
          ? "Failed to update Priority"
          : field === "summary"
          ? "Failed to update Title"
          : "Failed to update Bug status";

      toast.error(t(errorMessage));
    }
  };

  const handleCreateTask = async (
    containerKey: string,
    sprintId?: string,
    taskType?: string
  ) => {
    if (backlogViewType === "board") {
      dispatch(setCreateTask(true));
      return;
    }

    const taskName = newTaskNames[containerKey];
    if (!taskName?.trim()) {
      // setIsCreatingTask((prev) => ({ ...prev, [containerKey]: false }));
      setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));
      return;
    }

    if (!id) {
      toast.error(t("Project ID is required"));
      return;
    }

    const todoStatusId = getTodoStatusId();
    if (!todoStatusId) {
      toast.error(t("TODO status not found"));
      return;
    }

    try {
      const selectedType = taskType || newTaskTypes[containerKey] || "STORY";
      const payload: any = {
        summary: taskName.trim(),
        type: selectedType,
        project: id,
        bugStatus: todoStatusId,
        severity: "Medium",
      };

      if (sprintId) {
        payload.currentSprint = sprintId;
      } else {
        payload.isBackLog = true;
      }

      await createBug(payload).unwrap();
      toast.success(t("Task created successfully"));

      if (sprintId) {
        const response = await getSprintTasks({
          id: id || "",
          forBoard: true,
          sprint: sprintId,
          ...filterPayload,
          page: 1,
          limit: 100,
        }).unwrap();
        setSprintTasksData((prev) => ({
          ...prev,
          [sprintId]: response,
        }));
      } else {
        refetchBugList();
      }

      // setIsCreatingTask((prev) => ({ ...prev, [containerKey]: false }));
      setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));
    } catch (error) {
      console.error("Error creating task:", error);
      toast.error(t("Failed to create task"));
    }
  };

  const handleCancelTaskCreation = (containerKey: string) => {
    setIsCreatingTask((prev) => ({ ...prev, [containerKey]: false }));
    setNewTaskNames((prev) => ({ ...prev, [containerKey]: "" }));
    setNewTaskTypes((prev) => ({ ...prev, [containerKey]: "STORY" }));
  };
  const handleNewTaskTypeChange = (containerKey: string, typeCode: string) => {
    setNewTaskTypes((prev) => ({ ...prev, [containerKey]: typeCode }));
  };

  const handleKeyPress = (
    event: React.KeyboardEvent,
    containerKey: string,
    sprintId?: string
  ) => {
    if (event.key === "Enter") {
      handleCreateTask(containerKey, sprintId);
    } else if (event.key === "Escape") {
      handleCancelTaskCreation(containerKey);
    }
  };

  const handleCreateSprint = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!id) {
      toast.error(t("Project ID is required"));
      return;
    }

    try {
      const payload = {
        project: id,
        name: `Sprint ${sprints.length + 1}`,
        status: "DRAFT",
      };

      await createSprint(payload).unwrap();
      toast.success(t("Sprint created successfully"));
      refetchSprints();
    } catch (error) {
      console.error("Error creating sprint:", error);
      toast.error(t("Failed to create sprint"));
    }
  };

  const handleStartSprint = async (event: React.MouseEvent, sprint: Sprint) => {
    event.stopPropagation();

    const sprintTasks =
      sprintTasksData[sprint._id]?.data?.decryptedData?.items || [];
    if (sprintTasks.length === 0) {
      toast.error(t("Cannot start sprint without tasks"));
      return;
    }

    setSelectedSprintForStart(sprint);
    setIsSprintStartModalOpen(true);
  };

  const handleSprintStart = async (data: any) => {
    try {
      const payload = {
        project: id,
        name: data.name,
        status: "ACTIVE",
        startDate: data.startDate,
        endDate: data.endDate,
        sprintId: data.sprintId,
        description: data.goal,
        duration: data.duration,
      };
      await updateSprint(payload).unwrap();
      toast.success(t("Sprint started successfully"));
      setIsSprintStartModalOpen(false);
      setSelectedSprintForStart(null);
      refetchSprints();
      dispatch(setSelectedItem("All Sprints"));
      dispatch(setBacklogViewType("allSprint"));
      dispatch(setViewType("allSprint"));
      navigate(`/defect-tracker/projects/${id}/sprints`);
    } catch (error) {
      console.error("Error starting sprint:", error);
      toast.error(t("Failed to start sprint"));
    }
  };

  const handleCloseSprintStartModal = () => {
    setIsSprintStartModalOpen(false);
    setSelectedSprintForStart(null);
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    rowId: string
  ) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuAnchorEl({
      element: event.currentTarget,
      position: { top: rect.bottom, left: rect.left },
    });
    setSelectedRowId(rowId);
    setActionTaskId(rowId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSprintMenuAnchorEl(null);
    setSelectedRowId(null);
    setActionTaskId(null);
  };

  const handleEditTask = (taskId: string) => {
    handleMenuClose();
    setSelectedTaskId(taskId);
    setIsTaskModalOpen(true);
  };

  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
    setSelectedTaskId(null);
  };

  const openDeleteModal = (taskId: string) => {
    handleMenuClose();
    setTaskToDelete(taskId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  const handleEditSprint = async (sprintId: string) => {
    const sprint = sprints.find((s: Sprint) => s._id === sprintId);
    if (sprint) {
      setSelectedSprintForEdit(sprint);
      setIsEditSprintModalOpen(true);
    }
  };

  // Close edit modal
  const handleCloseEditSprintModal = () => {
    setIsEditSprintModalOpen(false);
    setSelectedSprintForEdit(null);
  };

  // Handle sprint update
  const handleSprintUpdate = async (data: any) => {
    try {
      const payload = {
        project: id,
        name: data.name,
        status: selectedSprintForEdit?.status === "DRAFT" ? "DRAFT" : "ACTIVE",
        startDate: data.startDate,
        endDate: data.endDate,
        sprintId: data.sprintId,
        description: data.goal,
        duration: data.duration,
      };

      await updateSprint(payload).unwrap();
      toast.success(t("Sprint updated successfully"));
      setIsEditSprintModalOpen(false);
      setSelectedSprintForEdit(null);
      refetchSprints();
    } catch (error) {
      console.error("Error updating sprint:", error);
      toast.error(t("Failed to update sprint"));
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      await deleteBug(taskToDelete).unwrap();
      toast.success(t("Task deleted successfully"));
      refetchBugList();
      Object.keys(sprintTasksData).forEach(async (sprintId) => {
        const response = await getSprintTasks({
          id: id || "",
          forBoard: true,
          sprint: sprintId,
          ...filterPayload,
          page: 1,
          limit: 100,
        }).unwrap();
        setSprintTasksData((prev) => ({
          ...prev,
          [sprintId]: response,
        }));
      });
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(t("Failed to delete task"));
      closeDeleteModal();
    }
  };

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordions((prev) => ({
        ...prev,
        [panel]: isExpanded,
      }));

      if (isExpanded && panel.startsWith("sprint-")) {
        const sprintId = panel.replace("sprint-", "");
        if (!sprintTasksData[sprintId]) {
          loadSprintTasks(sprintId);
        }
      }
    };

  const backlogRows = useMemo(() => {
    if (!listOfBugsData?.data?.decryptedData?.items) return [];

    return listOfBugsData?.data?.decryptedData?.items?.map((bug: BugItem) => ({
      id: bug._id,
      ...bug,
    }));
  }, [listOfBugsData]);

  const backlogTotalCount = listOfBugsData?.data?.decryptedData?.total || 0;

  useEffect(() => {
    if (listOfBugsData?.data?.decryptedData?.items) {
      setSprintTasksData((prev) => {
        // Get current local items for backlog
        const currentLocalItems =
          prev["backlog"]?.data?.decryptedData?.items || [];
        const incomingItems = listOfBugsData.data.decryptedData.items;
        const incomingTotal = listOfBugsData.data.decryptedData.total;

        if (currentLocalItems.length > incomingItems.length) {
          // Create a map of incoming items for fast lookup
          const incomingMap = new Map(
            incomingItems.map((item: any) => [item._id, item])
          );

          const mergedItems = currentLocalItems.map(
            (item: any) => incomingMap.get(item._id) || item
          );

          return {
            ...prev,
            backlog: {
              ...prev["backlog"],
              data: {
                ...prev["backlog"]?.data,
                decryptedData: {
                  ...prev["backlog"]?.data?.decryptedData,
                  items: mergedItems,
                  total: incomingTotal,
                },
              },
            },
          };
        }

        return {
          ...prev,
          backlog: listOfBugsData,
        };
      });
    }
  }, [listOfBugsData]);

  const backlogBoardData =
    sprintTasksData["backlog"]?.data?.decryptedData?.items || [];
  const backlogBoardTotal =
    sprintTasksData["backlog"]?.data?.decryptedData?.total || 0;

  const handleLoadMore = useCallback(
    async (columnId: string) => {
      const currentState = columnPaginationState[columnId];

      if (!currentState || currentState.isLoadingMore) return;

      let currentDataItems = [];
      let currentTotal = 0;

      if (columnId === "backlog") {
        currentDataItems =
          sprintTasksData["backlog"]?.data?.decryptedData?.items ||
          listOfBugsData?.data?.decryptedData?.items ||
          [];
        currentTotal =
          sprintTasksData["backlog"]?.data?.decryptedData?.total ||
          listOfBugsData?.data?.decryptedData?.total ||
          0;
      } else if (columnId.startsWith("sprint-")) {
        const sprintId = columnId.replace("sprint-", "");
        currentDataItems =
          sprintTasksData[sprintId]?.data?.decryptedData?.items || [];
        currentTotal =
          sprintTasksData[sprintId]?.data?.decryptedData?.total || 0;
      }

      if (currentTotal > 0 && currentDataItems.length >= currentTotal) {
        return;
      }

      setColumnPaginationState((prev) => ({
        ...prev,
        [columnId]: { ...prev[columnId], isLoadingMore: true },
      }));

      try {
        const nextPage = currentState.page + 1;
        const currentLimit = currentState.limit;

        let response;
        let dataKey = columnId;

        if (columnId === "backlog") {
          response = await getSprintTasks({
            id: id!,
            forBoard: true,
            ...filterPayload,
            page: nextPage,
            limit: currentLimit,
            isBackLog: true,
            sortType: "DESC",
            showEpic: true,
          }).unwrap();
        } else if (columnId.startsWith("sprint-")) {
          const sprintId = columnId.replace("sprint-", "");
          dataKey = sprintId;
          response = await getSprintTasks({
            id: id!,
            forBoard: true,
            sprint: sprintId,
            ...filterPayload,
            page: nextPage,
            limit: currentLimit,
          }).unwrap();
        }

        if (response) {
          const newTasks = response.data.decryptedData.items || [];

          if (newTasks.length === 0) {
            setColumnPaginationState((prev) => ({
              ...prev,
              [columnId]: { ...prev[columnId], isLoadingMore: false },
            }));
            return;
          }

          setSprintTasksData((prev) => {
            const existingSprintData = prev[dataKey];
            const existingItems =
              existingSprintData?.data?.decryptedData?.items ||
              (dataKey === "backlog"
                ? listOfBugsData?.data?.decryptedData?.items
                : []) ||
              [];

            const existingIds = new Set(existingItems.map((t: any) => t._id));
            const uniqueNewTasks = newTasks.filter(
              (t: any) => !existingIds.has(t._id)
            );

            return {
              ...prev,
              [dataKey]: {
                ...existingSprintData,
                data: {
                  ...existingSprintData?.data,
                  decryptedData: {
                    ...existingSprintData?.data?.decryptedData,
                    items: [...existingItems, ...uniqueNewTasks],
                    total: response.data.decryptedData.total,
                  },
                },
              },
            };
          });

          setColumnPaginationState((prev) => ({
            ...prev,
            [columnId]: {
              ...prev[columnId],
              page: nextPage,
              limit: currentLimit,
              isLoadingMore: false,
            },
          }));
        }
      } catch (error) {
        console.error("Error loading more tasks:", error);
        toast.error(t("Failed to load more tasks"));
        setColumnPaginationState((prev) => ({
          ...prev,
          [columnId]: { ...prev[columnId], isLoadingMore: false },
        }));
      }
    },
    [
      columnPaginationState,
      getSprintTasks,
      id,
      filterPayload,
      t,
      sprintTasksData,
      listOfBugsData,
    ]
  );

  const selectedTask = findTask(selectedTaskId || "");

  const isGlobalLoading = () => {
    return (
      isBugsLoadingQuery || isBugsFetchingQuery || isDeleting || isUpdating
    );
  };

  const renderBacklogAccordion = () => (
    // <TaskAccordion
    <TaskDataGrid
      accordionKey="backlog"
      droppableId="backlog"
      title={`${t("Backlog")}`}
      isExpanded={expandedAccordions.backlog}
      onAccordionChange={handleAccordionChange("backlog")}
      tasks={backlogRows}
      totalCount={backlogTotalCount}
      isLoading={isGlobalLoading()}
      paginationModel={paginationModel}
      setPaginationModel={setPaginationModel}
      onTaskClick={handleEditTask}
      onMenuClick={handleMenuClick}
      isCreatingTask={isCreatingTask.backlog || false}
      newTaskName={newTaskNames.backlog || ""}
      onCreateTaskClick={() => handleCreateTaskClick("backlog")}
      onTaskNameChange={(name) =>
        setNewTaskNames((prev) => ({ ...prev, backlog: name }))
      }
      onCreateTask={() => handleCreateTask("backlog")}
      onCancelTaskCreation={() => handleCancelTaskCreation("backlog")}
      onKeyPress={(e) => handleKeyPress(e, "backlog")}
      isCreatingBug={isCreatingBug}
      newTaskType={newTaskTypes.backlog || "STORY"}
      onNewTaskTypeChange={(typeCode) =>
        handleNewTaskTypeChange("backlog", typeCode)
      }
      headerButtons={[
        {
          label: t("Create Task"),
          icon: <AddIcon />,
          onClick: () => handleCreateTaskClick("backlog"),
          disabled: isCreatingBug,
        },
        {
          label: isCreatingSprint ? t("Creating...") : t("Create Sprint"),
          icon: <AddIcon />,
          onClick: handleCreateSprint,
          disabled: isCreatingSprint,
        },
      ]}
      emptyMessage={t("No tasks found in backlog")}
      isDragging={isDragging}
      selectedTasks={containerSelections.backlog || new Set()}
      onSelectionChange={handleSelectionChange("backlog")}
      isSelectionDisabled={
        activeContainer !== null && activeContainer !== "backlog"
      }
      onStatusClick={handleStatusClick}
      editingStatusTaskId={editingStatusTaskId}
      statusList={statusList}
      isUpdatingStatus={isUpdating}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      availableTypes={projectDetails?.itemTypes || issueType}
      availableUsers={companyMemberData?.filter((x) =>
        [...(projectDetails?.users || []), projectDetails?.owner].includes(
          x._id
        )
      )}
      onTaskUpdate={(taskId, updatedTask, field) =>
        handleTaskUpdate(taskId, updatedTask, field, "backlog")
      }
    />
  );

  // Render Sprint Accordions Component
  const renderSprintAccordions = () => (
    <Box sx={{ mb: 2 }}>
      {isSprintLoading ? (
        <Box sx={{ mb: 2 }}>
          {[...Array(2)].map((_, index) => (
            <Paper key={index} sx={{ mb: 2, p: 2 }}>
              <Skeleton variant="text" width="30%" height={32} />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                sx={{ mt: 1 }}
              />
            </Paper>
          ))}
        </Box>
      ) : (
        sprints.map((sprint: Sprint) => {
          const sprintKey = `sprint-${sprint._id}`;
          const droppableId = sprintKey;
          const isExpanded = expandedAccordions[sprintKey] || false;
          const sprintTasks =
            sprintTasksData[sprint._id]?.data?.decryptedData?.items || [];
          const sprintTaskCount =
            sprintTasksData[sprint._id]?.data?.decryptedData?.total || 0;
          const isSprintTasksLoading =
            isExpanded && !sprintTasksData[sprint._id];
          const sprintPagination = sprintPaginationModels[sprint._id] || {
            page: 1,
            pageSize: 10,
          };

          return (
            <TaskAccordion
              key={sprint._id}
              accordionKey={sprintKey}
              droppableId={droppableId}
              title={sprint.name}
              isExpanded={isExpanded}
              onAccordionChange={handleAccordionChange(sprintKey)}
              tasks={sprintTasks}
              totalCount={sprintTaskCount}
              isLoading={isSprintTasksLoading || isUpdating}
              paginationModel={sprintPagination}
              setPaginationModel={handleSprintPaginationChange(sprint._id)}
              onTaskClick={handleEditTask}
              onMenuClick={handleMenuClick}
              isCreatingTask={isCreatingTask[sprintKey] || false}
              newTaskName={newTaskNames[sprintKey] || ""}
              onCreateTaskClick={() => handleCreateTaskClick(sprintKey)}
              onTaskNameChange={(name) =>
                setNewTaskNames((prev) => ({
                  ...prev,
                  [sprintKey]: name,
                }))
              }
              onCreateTask={() => handleCreateTask(sprintKey, sprint._id)}
              onCancelTaskCreation={() => handleCancelTaskCreation(sprintKey)}
              onKeyPress={(e) => handleKeyPress(e, sprintKey, sprint._id)}
              isCreatingBug={isCreatingBug}
              onDeleteSprint={openDeleteSprintModal}
              onEditSprint={handleEditSprint}
              isDeletingSprint={isDeletingSprint}
              headerButtons={[
                {
                  label:
                    sprint.status === "DRAFT"
                      ? t("Start Sprint")
                      : t("Complete Sprint"),
                  icon:
                    sprint.status === "DRAFT" ? <PlayArrowIcon /> : <AddIcon />,
                  onClick: (e) => {
                    if (sprint.status === "DRAFT") {
                      handleStartSprint(e, sprint);
                    } else {
                      handleCompleteSprint(e, sprint);
                    }
                  },
                  disabled:
                    sprint.status === "DRAFT" && sprintTasks.length === 0,
                },
              ]}
              sprint={sprint}
              emptyMessage={t("No tasks found in this sprint")}
              isDragging={isDragging}
              selectedTasks={containerSelections[sprintKey] || new Set()}
              onSelectionChange={handleSelectionChange(sprintKey)}
              isSelectionDisabled={
                activeContainer !== null && activeContainer !== sprintKey
              }
              onStatusClick={handleStatusClick}
              editingStatusTaskId={editingStatusTaskId}
              statusList={statusList}
              isUpdatingStatus={isUpdating}
              onTaskUpdate={(taskId, updatedTask, field) =>
                handleTaskUpdate(taskId, updatedTask, field, sprintKey)
              }
            />
          );
        })
      )}
    </Box>
  );

  // List View (Original Implementation)
  const renderListView = () => (
    <Box
      sx={{
        flex: 1,
        overflow: "auto",
        paddingRight: 1,
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: theme.palette.grey[100],
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: theme.palette.grey[400],
          borderRadius: "4px",
          "&:hover": {
            background: theme.palette.grey[600],
          },
        },
      }}
    >
      {renderSprintAccordions()}
      {renderBacklogAccordion()}
    </Box>
  );

  // Table View (Side by Side Layout)
  const renderTableView = () => (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        gap: 2,
        overflow: "hidden",
      }}
    >
      {/* Left Side - Backlog */}
      <Box
        sx={{
          flex: isSprintSectionCollapsed ? 1 : 1.2,
          minWidth: 0,
          overflow: "auto",
          paddingRight: 1,
          transition: "flex 0.3s ease",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: theme.palette.grey[100],
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: theme.palette.grey[400],
            borderRadius: "4px",
            "&:hover": {
              background: theme.palette.grey[600],
            },
          },
        }}
      >
        {renderBacklogAccordion()}
      </Box>

      {/* Right Side - Sprints with Toggle Button */}
      <Box
        sx={{
          flex: isSprintSectionCollapsed ? 0 : 1,
          minWidth: isSprintSectionCollapsed ? "auto" : 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          transition: "flex 0.3s ease",
          mr: 2,
        }}
      >
        {/* Sprint Section Toggle Button */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: isSprintSectionCollapsed ? -14 : 16, // Slightly more visible when collapsed
            zIndex: 10,
            transition: "right 0.3s ease",
            px: isSprintSectionCollapsed ? 0 : 2,
          }}
        >
          <Tooltip
            title={
              isSprintSectionCollapsed ? t("Show Sprints") : t("Hide Sprints")
            }
            placement="top"
          >
            <IconButton
              onClick={() =>
                setIsSprintSectionCollapsed(!isSprintSectionCollapsed)
              }
              size="small"
              sx={{
                backgroundColor: isSprintSectionCollapsed
                  ? theme.palette.primary.main
                  : theme.palette.background.paper,
                color: isSprintSectionCollapsed
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
                border: `1px solid ${
                  isSprintSectionCollapsed
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }`,
                "&:hover": {
                  backgroundColor: isSprintSectionCollapsed
                    ? theme.palette.primary.dark
                    : theme.palette.action.hover,
                  boxShadow: theme.shadows[4],
                },
                transform: isSprintSectionCollapsed
                  ? "rotate(180deg) scale(1.1)" // Slightly larger when collapsed
                  : "rotate(0deg)",
                transition: "all 0.3s ease",
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Sprints Content */}
        {!isSprintSectionCollapsed && (
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              paddingRight: 1,
              animation: "slideInFromRight 0.3s ease",
              "@keyframes slideInFromRight": {
                from: {
                  opacity: 0,
                  transform: "translateX(20px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateX(0)",
                },
              },
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.grey[100],
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.grey[400],
                borderRadius: "4px",
                "&:hover": {
                  background: theme.palette.grey[600],
                },
              },
            }}
          >
            {/* Sprints Header */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                py: 1.5,
                pr: 7, // Add right padding to avoid overlap with toggle button
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {t("Sprints")} ({sprints.length})
              </Typography>
            </Box>
            {renderSprintAccordions()}
          </Box>
        )}
      </Box>
    </Box>
  );
  const renderBoardView = () => {
    return (
      <BacklogBoardView
        backlogRows={
          sprintTasksData["backlog"]?.data?.decryptedData?.items || backlogRows
        }
        sprints={sprints}
        sprintTasksData={sprintTasksData}
        isLoading={isGlobalLoading()}
        onTaskClick={handleEditTask}
        onMenuClick={handleMenuClick}
        onCreateTask={(sprintId) =>
          handleCreateTask(
            sprintId ? `sprint-${sprintId}` : "backlog",
            sprintId
          )
        }
        onCreateSprint={handleCreateSprint}
        onStartSprint={handleStartSprint}
        onCompleteSprint={handleCompleteSprint}
        handleDragStart={handleDragStart}
        handleDragOver={handleDragOver}
        handleDragEnd={handleDragEnd}
        activeTask={activeTask}
        findTask={findTask}
        statusList={statusList}
        companyMemberData={companyMemberData}
        onTaskUpdate={handleTaskUpdate}
        refreshColumn={refetchBugList}
        minimizedColumns={minimizedColumns}
        onToggleMinimize={handleToggleMinimize}
        onLoadMore={handleLoadMore}
        columnPaginationState={columnPaginationState}
        backlogTotalCount={
          sprintTasksData["backlog"]?.data?.decryptedData?.total ||
          backlogTotalCount
        }
        sprintTotalCounts={Object.fromEntries(
          sprints.map((sprint: Sprint) => [
            sprint._id,
            sprintTasksData[sprint._id]?.data?.decryptedData?.total || 0,
          ])
        )}
      />
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Box
        sx={{
          height: "calc(100vh - 250px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header with selection info */}
        {totalSelectedTasks > 0 && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body1">
              {t("{{count}} task(s) selected", { count: totalSelectedTasks })}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setMoveToMenuAnchorEl(e.currentTarget)}
                disabled={getAvailableMoveDestinations().length === 0}
                endIcon={<ArrowDropDownIcon />}
                sx={{
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.contrastText,
                }}
              >
                {t("Move to")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={clearAllSelections}
                sx={{
                  color: theme.palette.primary.contrastText,
                  borderColor: theme.palette.primary.contrastText,
                }}
              >
                {t("Clear Selection")}
              </Button>
            </Box>
          </Box>
        )}

        {/* Content based on view type */}
        {backlogViewType === "list" ? renderListView() : renderTableView()}
        {backlogViewType === "board" ? renderBoardView() : null}

        {/* Task Modal */}

        {/* Drag Overlay - Shows the task(s) being dragged */}
        <DragOverlay>
          {activeTask && draggedTasks.length > 0 ? (
            <Paper
              sx={{
                p: 2,
                minWidth: 300,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[8],
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 1,
                opacity: 0.9,
              }}
            >
              {draggedTasks.length === 1 ? (
                <Typography variant="body2" fontWeight={500}>
                  {activeTask.bugKey && (
                    <span style={{ marginRight: "4px", color: "#1976D2" }}>
                      {activeTask.bugKey}:
                    </span>
                  )}
                  {activeTask.summary}
                </Typography>
              ) : (
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    {t("Moving {{count}} tasks", {
                      count: draggedTasks.length,
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeTask.bugKey && (
                      <span style={{ marginRight: "4px", color: "#1976D2" }}>
                        {activeTask.bugKey}:
                      </span>
                    )}
                    {activeTask.summary}
                    {draggedTasks.length > 1 && " + others..."}
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : null}
        </DragOverlay>

        <BacklogMenus
          // Main context menu props
          menuAnchorEl={menuAnchorEl}
          onMenuClose={handleMenuClose}
          actionTaskId={actionTaskId}
          onEditTask={handleEditTask}
          onDeleteTask={openDeleteModal}
          // Sprint submenu props
          sprintMenuAnchorEl={sprintMenuAnchorEl}
          onSprintMenuClose={() => setSprintMenuAnchorEl(null)}
          onSprintMenuOpen={(e) => setSprintMenuAnchorEl(e.currentTarget)}
          availableSprints={getAvailableSprints()}
          onMoveToSprint={handleMoveToSprint}
          isBacklogTask={isBacklogTask}
          isUpdating={isUpdating}
          // Move to menu props
          moveToMenuAnchorEl={moveToMenuAnchorEl}
          onMoveToMenuClose={() => setMoveToMenuAnchorEl(null)}
          availableMoveDestinations={getAvailableMoveDestinations()}
          onBulkMove={handleBulkMove}
          isUpdatingSprintStatus={isUpdatingSprintStatus}
          sprints={sprints}
        />

        {/* Status Selection Menu */}
        <Menu
          anchorEl={statusAnchorEl}
          open={Boolean(statusAnchorEl)}
          onClose={handleStatusClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              minWidth: 160,
              maxHeight: 300,
            },
          }}
        >
          {statusList?.map((status: any) => (
            <MenuItem
              key={status._id}
              onClick={() => handleStatusUpdate(status._id)}
              disabled={isUpdating}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 1,
              }}
            >
              {/* <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  backgroundColor: status.color || theme.palette.grey[400],
                  flexShrink: 0,
                }}
              /> */}
              <Typography variant="body2" sx={{ flex: 1 }}>
                {status.status}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        {/* Task Update Drawer */}
        {selectedTaskId && isTaskModalOpen && (
          <TaskUpdateDrawer
            open={isTaskModalOpen}
            onClose={handleCloseTaskModal}
            setBugId={() => {}}
            setDrawerOpen={() => {}}
            bugId={selectedTaskId}
            users={users?.data?.decryptedData || []}
            createdBy={selectedTask?.createdBy?.fullName || ""}
            setCreatedBy={() => {}}
            refreshColumn={() => {
              refetchBugList();
              Object.keys(sprintTasksData).forEach(async (sprintId) => {
                const response = await getSprintTasks({
                  id: id || "",
                  forBoard: true,
                  sprint: sprintId,
                  ...filterPayload,
                  page: 1,
                  limit: 100,
                }).unwrap();
                setSprintTasksData((prev) => ({
                  ...prev,
                  [sprintId]: response,
                }));
              });
            }}
          />
        )}

        {/* Task Delete Confirmation Modal */}
        <DeleteConfirmModal
          open={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onConfirm={handleDeleteTask}
          loading={isDeleting}
          title={t("Delete Task")}
          confirmMessage={t("Are you sure you want to delete this task?")}
          okText={t("Delete")}
        />

        {/* Sprint Delete Confirmation Modal */}
        <DeleteConfirmModal
          open={isDeleteSprintModalOpen}
          onClose={closeDeleteSprintModal}
          onConfirm={handleConfirmDeleteSprint}
          loading={isDeletingSprint}
          title={t("Delete Sprint")}
          confirmMessage={
            sprintToDelete
              ? t(
                  "Are you sure you want to delete sprint 'test'? This action cannot be undone.",
                  {
                    sprintName: sprintToDelete.name,
                  }
                )
              : t("Are you sure you want to delete this sprint?")
          }
          okText={t("Delete")}
        />

        {/* Sprint Start Modal */}
        {selectedSprintForStart && (
          <SprintStartModal
            open={isSprintStartModalOpen}
            onClose={handleCloseSprintStartModal}
            onStart={handleSprintStart}
            sprint={selectedSprintForStart}
            taskCount={
              sprintTasksData[selectedSprintForStart._id]?.data?.decryptedData
                ?.total || 0
            }
            isLoading={isUpdatingSprint}
          />
        )}

        {/* Sprint Edit Modal */}
        {selectedSprintForEdit && (
          <SprintStartModal
            open={isEditSprintModalOpen}
            onClose={handleCloseEditSprintModal}
            onStart={handleSprintUpdate}
            sprint={selectedSprintForEdit}
            taskCount={
              sprintTasksData[selectedSprintForEdit._id]?.data?.decryptedData
                ?.total || 0
            }
            isLoading={isUpdatingSprint}
            isEditMode={true}
          />
        )}

        {/* Sprint Complete Modal */}
        {selectedSprintForComplete && (
          <SprintCompleteModal
            open={isSprintCompleteModalOpen}
            onClose={handleCloseSprintCompleteModal}
            onComplete={handleSprintComplete}
            sprint={selectedSprintForComplete?._id}
            draftSprints={getDraftSprints()}
            isLoading={isUpdatingSprintStatus}
          />
        )}
      </Box>
    </DndContext>
  );
};

export default BacklogTab;
